import { Bar, Line, Scatter, Pie } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from "chart.js";
import { formatDialect, spark } from "sql-formatter";
import * as React from "react";
import { Pagination, Popover, Table, Skeleton } from "antd";
import { CSVLink } from "react-csv";
import { ColumnsType } from "antd/lib/table/interface";
import IconTextButton from "../iconTextButton/IconTextButton";
import { SearchRequests } from "../../services/SearchRequests";
import dayjs from "dayjs";
import { ThumbDislikeRegular, ThumbLikeRegular } from "@fluentui/react-icons";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

interface IAiTableData {
    statementId?: string;
    dataArray?: any[];
    columnNames?: string[];
}

interface IAiChatMessage {
    id: string;
    serverMessageId?: string;
    role: "user" | "assistant";
    text?: string;
    tables?: IAiTableData[];
    suggestedQuestions?: string[];
    error?: string;
    loading?: boolean;
    progressStage?: number;
}

interface IAiSearchViewProps {
    searchDisabled?: boolean;
    dataSource?: string;
    pageName?: string;
    getWorkDetails?: (dataSource: string, workID: number, isForWorkflowSession?: boolean) => void;
    getProductDetails?: (dataSource: string, productCoreID: number, isForWorkflowSession?: boolean, openNewTab?: boolean, id1?: string, id2?: string, type?: string, originOfRevision?: string, productionType?: string, musicDuration?: number[], lastModifiedDate?: Date) => void;
    getUsageDetails?: (usageID: string, lookupValues?: any, openEntityForWorkflowSession?: boolean) => void;
}

interface IAiSearchViewState {
    aiPrompt: string;
    aiMessages: IAiChatMessage[];
    inputAtBottom: boolean;
    tablePagination: IAiTablePaginationState;
    conversationId?: string;
    feedbackState: { [key: string]: { feedback?: 'up' | 'down', comment?: string, submitted?: boolean } };
}


interface IAiTablePaginationState {
    [key: string]: { page: number; pageSize: number };
}

interface IAiSearchViewTableCodeState {
    [key: string]: boolean;
}

class AiSearchView extends React.PureComponent<IAiSearchViewProps, IAiSearchViewState> {
    aiMessagesRef = React.createRef<HTMLDivElement>();
    progressTimer: NodeJS.Timeout | null = null;
    progressStages = [
        "Understanding the question...",
        "Executing the query...",
        "Compiling the response data...",
        "Finalizing the results...",
        "Taking longer than expected..."
    ];
    state: IAiSearchViewState = {
        aiPrompt: "",
        aiMessages: [] as IAiChatMessage[],
        inputAtBottom: false,
        tablePagination: {},
        conversationId: undefined,
        feedbackState: {},
    };

    tableCodeVisibility: IAiSearchViewTableCodeState = {};

    componentDidUpdate = (prevProps: IAiSearchViewProps, prevState: IAiSearchViewState) => {
        if ((prevState.aiMessages?.length || 0) !== (this.state.aiMessages?.length || 0)) {
            const node = this.aiMessagesRef.current;
            if (node) {
                node.scrollTop = node.scrollHeight;
            }
        }
    };

    createMessageId = (prefix: string) => {
        return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    };

    getPromptWithSource = (prompt: string) => {
        const dataSource = (this.props.dataSource || "").trim();
        const context = [dataSource].filter(Boolean).join(" ");
        return context ? `${dataSource} ${prompt}` : prompt;
    };

    buildAiTables = (tabularData: any): (IAiTableData & { query?: string })[] => {
        if (!Array.isArray(tabularData)) {
            return [];
        }

        return tabularData
            .map((item: any) => ({
                statementId: item?.statementId ?? item?.StatementId,
                dataArray: item?.dataArray ?? item?.DataArray,
                columnNames: item?.columnNames ?? item?.ColumnNames,
                query: item?.query ?? item?.Query
            }))
            .filter((item: IAiTableData) => Array.isArray(item.dataArray) && item.dataArray.length > 0);
    };

    normalizeResponse = (response: any) => {
        const textContent = response?.textContent ?? response?.TextContent ?? "";
        const suggestedQuestions = response?.suggestedQuestions ?? response?.SuggestedQuestions ?? [];
        const tabularData = response?.tabularData ?? response?.TabularData ?? [];

        return {
            textContent: textContent || "",
            suggestedQuestions: Array.isArray(suggestedQuestions) ? suggestedQuestions : [],
            aiTables: this.buildAiTables(tabularData)
        };
    };

    updateMessage = (messages: IAiChatMessage[], messageId: string, updates: Partial<IAiChatMessage>) => {
        return (messages || []).map((message) =>
            message.id === messageId
                ? { ...message, ...updates }
                : message
        );
    };

    handleSubmit = () => {
        if (this.props.searchDisabled) return;

        const runSearch = () => this.runAiSearch(this.state.aiPrompt);

        this.state.inputAtBottom
            ? runSearch()
            : this.setState({ inputAtBottom: true }, runSearch);
    };

    runAiSearch = async (prompt: string) => {
        const trimmedPrompt = (prompt || "").trim();
        if (!trimmedPrompt) return;

        const promptWithSource = this.getPromptWithSource(trimmedPrompt);
        const userMessage: IAiChatMessage = {
            id: this.createMessageId("user"),
            role: "user",
            text: trimmedPrompt
        };
        const assistantMessageId = this.createMessageId("assistant");
        const assistantMessage: IAiChatMessage & { progressStage?: number } = {
            id: assistantMessageId,
            role: "assistant",
            loading: true,
            progressStage: 0
        };

        this.setState(prevState => ({
            aiPrompt: "",
            aiMessages: [...(prevState.aiMessages || []), userMessage, assistantMessage]
        }), () => this.startProgressTimer(assistantMessageId));

        try {
            const response = await SearchRequests.askGenie(promptWithSource, this.props.pageName, this.state.conversationId);
            const { textContent, suggestedQuestions, aiTables } = this.normalizeResponse(response);
            const conversationId = response?.conversationId || this.state.conversationId;
            this.setState(prevState => ({
                aiMessages: this.updateMessage(prevState.aiMessages || [], assistantMessageId, {
                    loading: false,
                    text: textContent,
                    tables: aiTables,
                    suggestedQuestions,
                    progressStage: undefined,
                    serverMessageId: response?.messageId
                }),
                conversationId
            }), this.stopProgressTimer);
        } catch (error: any) {
            const message = error?.response?.data
                ? JSON.stringify(error.response.data, null, 2)
                : (error?.message || "AI request failed.");
            this.setState(prevState => ({
                aiMessages: this.updateMessage(prevState.aiMessages || [], assistantMessageId, {
                    loading: false,
                    error: message,
                    progressStage: undefined
                })
            }), this.stopProgressTimer);
        }
    };

    startProgressTimer = (assistantMessageId: string) => {
        this.stopProgressTimer();
        let stage = 0;
        this.progressTimer = setInterval(() =>
            this.setState(prevState => {
                stage = Math.min(stage + 1, 4);
                return {
                    aiMessages: prevState.aiMessages.map(msg =>
                        msg.id === assistantMessageId && msg.loading
                            ? { ...msg, progressStage: stage }
                            : msg
                    )
                };
            })
            , 5000);
    };

    stopProgressTimer = () => {
        if (this.progressTimer) {
            clearInterval(this.progressTimer);
            this.progressTimer = null;
        }
    };

    clearAiConversation = () => {
        this.setState({
            aiPrompt: "",
            aiMessages: [],
            inputAtBottom: false,
            conversationId: undefined,
        });
    };

    getAiTableCsvData = (table: IAiTableData) => {
        const rows = Array.isArray(table?.dataArray) ? table.dataArray : [];
        const columnCount = rows.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 0), 0);
        const headers = (table?.columnNames && table.columnNames.length)
            ? table.columnNames.slice(0, columnCount)
            : Array.from({ length: columnCount }, (_, i) => `Col ${i + 1}`);
        const data = rows.map(row =>
            Array.from({ length: columnCount }, (_, i) => this.formatCellValue(Array.isArray(row) ? row[i] : "", { forCsv: true }))
        );
        return [headers, ...data];
    };

    formatCellValue = (value: any, options?: { colName?: string, entityType?: string, forCsv?: boolean }) => {
        // JSON pretty print
        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value);
                if (typeof parsed === "object" && parsed !== null) {
                    if (options?.forCsv) { return JSON.stringify(parsed); }
                    const prettyJson = JSON.stringify(parsed, null, 2);
                    return (
                        <Popover content={<pre className="aiJsonPopover">{prettyJson}</pre>} placement="topRight">
                            <pre className="aiJsonCell" aria-label="JSON cell content">{prettyJson}</pre>
                        </Popover>
                    );
                }
            } catch { }
        }
        // Date formatting
        if (typeof value === "string" && dayjs(value).isValid() && value.includes("T")) {
            return dayjs(value).format("YYYY-MM-DD HH:mm:ss.SSS");
        }
        // Bold formatting for text
        if (typeof value === "string" && value.includes("**")) {
            return value.split("**").map((segment, i) =>
                i % 2 ? <strong key={`ai-bold-${i}`}>{segment}</strong> : segment
            );
        }
        // ID clickable links
        const { colName, entityType, forCsv } = options || {};
        const idColNames = ["Id", "Work ID", "Product ID", "Usage ID"];
        if (colName && idColNames.includes(colName) && value && !forCsv) {
            const handlers: Record<string, ((e: React.MouseEvent) => void) | undefined> = {
                works: this.props.getWorkDetails && Number(value)
                    ? (e) => { e.preventDefault(); this.props.getWorkDetails(this.props.dataSource || "Repertoire", Number(value)); }
                    : undefined,
                products: this.props.getProductDetails && Number(value)
                    ? (e) => { e.preventDefault(); this.props.getProductDetails(this.props.dataSource || "Repertoire", Number(value)); }
                    : undefined,
                usages: this.props.getUsageDetails
                    ? (e) => { e.preventDefault(); this.props.getUsageDetails(String(value)); }
                    : undefined
            };
            const onClick = handlers[entityType || ""];
            if (onClick) {
                return (<a href="#" onClick={onClick} aria-label={`Go to details for ${colName}: ${value}`} role="link"> {value} </a>);
            }
        }

        return value;
    };

    renderAiTable = (table: IAiTableData & { query?: string }, index: number) => {
        const tableKey = table.statementId || `table-${index}`;
        const { tablePagination } = this.state as any;
        const page = tablePagination?.[tableKey]?.page || 1;
        const pageSize = tablePagination?.[tableKey]?.pageSize || 10;
        const rows = Array.isArray(table?.dataArray) ? table.dataArray : [];
        const columnCount = Math.max(0, ...rows.filter(Array.isArray).map(row => row.length));

        if (columnCount === 0) {
            return (
                <div key={`ai-table-${index}`} className="aiTableWrapper">
                    <div className="aiBubbleContent">No tabular rows.</div>
                </div>
            );
        }

        let entityType: "works" | "products" | "usages" = "works";
        const propSource = (this.props.pageName || "").toLowerCase();
        if (["works", "products", "usages"].includes(propSource)) {
            entityType = propSource as any;
        }

        const columns: ColumnsType<Record<string, any>> = Array.from({ length: columnCount }, (_, colIndex) => ({
            title: (
                <span>
                    <span>{table?.columnNames?.[colIndex] || `Col ${colIndex + 1}`}</span>
                    <span className="visually-hidden" role="columnheader" aria-label={table?.columnNames?.[colIndex] || `Column ${colIndex + 1}`}></span>
                </span>
            ),
            dataIndex: `col${colIndex}`,
            key: `col${colIndex}`,
            ellipsis: true,
            render: (value: any) => this.formatCellValue(value, { colName: table?.columnNames?.[colIndex], entityType }),
            sorter: (a: Record<string, any>, b: Record<string, any>) => {
                const valA = a[`col${colIndex}`];
                const valB = b[`col${colIndex}`];

                if (valA == null) return -1;
                if (valB == null) return 1;

                const numA = Number(String(valA).replace(/,/g, ''));
                const numB = Number(String(valB).replace(/,/g, ''));

                if (!isNaN(numA) && !isNaN(numB)) {
                    return numA - numB;
                }

                return String(valA)
                    .toLowerCase()
                    .localeCompare(String(valB).toLowerCase());
            }
        }));

        const dataSource = rows.map((row: any, rowIndex: number) => {
            const record: Record<string, any> = { key: rowIndex };
            for (let colIndex = 0; colIndex < columnCount; colIndex += 1) {
                const cellValue = Array.isArray(row) ? row[colIndex] : undefined;
                record[`col${colIndex}`] = cellValue;
            }
            return record;
        });

        const pagedData = dataSource.slice((page - 1) * pageSize, page * pageSize);
        const handlePaginationChange = (p: number, ps: number) => {
            this.setState(prevState => ({
                tablePagination: {
                    ...prevState.tablePagination,
                    [tableKey]: { page: p, pageSize: ps }
                }
            }));
        };

        const isCodeVisible = this.tableCodeVisibility[tableKey] || false;
        const handleShowCode = () => {
            this.tableCodeVisibility[tableKey] = !isCodeVisible;
            this.forceUpdate();
        };

        return (
            <div key={`ai-table-${index}`} className="aiTableWrapper aiTableWrapperNoPad">
                <div className="aiTableActions aiTableActionsSticky">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                        <CSVLink
                            data={this.getAiTableCsvData(table)}
                            filename={`ai-table-${index + 1}.csv`}
                            className="aiTableExportBtn"
                            target="_blank"
                        >
                            <IconTextButton
                                icon={"icon ms-Icon ms-Icon--Download"}
                                tooltip="Export CSV"
                                text={"Export"}
                            />
                        </CSVLink>
                        {table.query && (
                            <button
                                type="button"
                                className="aiTableShowCodeBtn"
                                onClick={handleShowCode}
                                title={isCodeVisible ? "Hide Code" : "Show Code"}
                            >
                                <span className="ms-Icon ms-Icon--Embed" style={{ fontSize: 15 }} />
                                {isCodeVisible ? "Hide Code" : "Show Code"}
                            </button>
                        )}
                    </div>
                </div>
                {table.query && isCodeVisible && (
                    <div className="aiTableQueryBlock" style={{ marginTop: 12 }}>
                        <pre className="aiSqlCodeBlock">
                            {formatDialect(String(table.query), { dialect: spark })}
                        </pre>
                    </div>
                )}
                <div className="aiTableScroll">
                    <Table
                        size="small"
                        pagination={false}
                        columns={columns}
                        dataSource={pagedData}
                        className="aiAntTableFullWidth"
                        scroll={{ x: 'max-content' }}
                    />
                </div>
                <div className="aiTableActions aiTableActionsBottomSticky">
                    <Pagination
                        current={page}
                        pageSize={pageSize}
                        total={dataSource.length}
                        showSizeChanger
                        pageSizeOptions={[10, 20, 50, 100]}
                        onChange={handlePaginationChange}
                        className="aiPaginationNoMargin"
                    />
                </div>
            </div>
        );
    };


    // Chart utilities
    chartColors = [
        'rgba(51,102,204,0.5)', 'rgba(220,57,18,0.5)', 'rgba(255,153,0,0.5)', 'rgba(16,150,24,0.5)',
        'rgba(153,0,153,0.5)', 'rgba(0,153,198,0.5)', 'rgba(221,68,119,0.5)', 'rgba(102,170,0,0.5)',
        'rgba(184,46,46,0.5)', 'rgba(49,99,149,0.5)', 'rgba(255,99,132,0.5)', 'rgba(54,162,235,0.5)',
        'rgba(255,206,86,0.5)', 'rgba(75,192,192,0.5)', 'rgba(153,102,255,0.5)', 'rgba(255,159,64,0.5)',
        'rgba(100,255,218,0.5)', 'rgba(255,99,255,0.5)', 'rgba(99,255,132,0.5)', 'rgba(255,219,88,0.5)',
        'rgba(0,204,153,0.5)', 'rgba(204,0,102,0.5)', 'rgba(102,51,153,0.5)', 'rgba(0,102,204,0.5)',
        'rgba(204,102,0,0.5)', 'rgba(0,204,255,0.5)', 'rgba(204,255,0,0.5)', 'rgba(255,0,127,0.5)',
        'rgba(127,0,255,0.5)', 'rgba(0,255,127,0.5)'
    ];
    truncateLabel = (label, maxLength = 12) => String(label).length > maxLength ? String(label).slice(0, maxLength) + "..." : String(label);

    looksLikeTime = (colName) => ["date", "time", "year", "month"].some(k => (colName || "").toLowerCase().includes(k));

    isNumber = (value, colName) => {
        if (typeof colName === 'string' && colName.toLowerCase().includes('id')) return false;
        return !isNaN(Number(value)) && value !== null && value !== '';
    };

    uniqueCount = (rows, col) => new Set(rows.map(r => r[col])).size;

    chooseChart = (columns, rows, userQuery = "") => {
        // Fallback to table if no data or only one row
        if (!columns || !rows || rows.length === 0 || rows.length === 1) return { type: "table" };

        // --- 1. Classify columns ---
        const sample = rows[0] || {};
        const timeCols = columns.filter(this.looksLikeTime);
        const numericCols = columns.filter(c => this.isNumber(sample[c], c));
        const catCols = columns.filter(c => !timeCols.includes(c) && !numericCols.includes(c));

        if (numericCols.length === 0) {
            return { type: "table" };
        }

        // 1 time + 1 numeric - line chart (trend over time)
        if (timeCols.length === 1 && numericCols.length === 1) {
            return { type: "line", x: timeCols[0], y: numericCols[0] };
        }

        // 1 categorical + 1 numeric representing proportion - pie chart
        if (catCols.length === 1 && numericCols.length >= 1) {
            for (const numCol of numericCols) {
                // Heuristic: if numeric column sums to ~1 or ~100, treat as proportion
                const total = rows.reduce((sum, r) => sum + (this.isNumber(r[numCol], numCol) ? Number(r[numCol]) : 0), 0);
                if ((total > 0 && total <= 1.1) || Math.abs(total - 100) < 5) {
                    return { type: "pie", label: catCols[0], value: numCol };
                }
            }
        }

        // 1 categorical + 1 numeric (≤20 categories) - bar chart
        if (catCols.length === 1 && numericCols.length === 1) {
            const uniqueCatCount = this.uniqueCount(rows, catCols[0]);
            if (uniqueCatCount <= 20) {
                return { type: "bar", x: catCols[0], y: numericCols[0] };
            } else {
                // Too many categories: fallback to table or top-N bar chart
                return { type: "table" };
            }
        }

        // ≥2 numeric fields - scatter plot (correlation)
        if (numericCols.length >= 2) {
            // Scatter only if both numeric columns have at least 5 unique values and at least half as many unique values as rows
            const unique = col => new Set(rows.map(r => r[col])).size;
            const a = numericCols[0], b = numericCols[1];
            if (unique(a) >= 5 && unique(b) >= 5 &&
                unique(a) >= rows.length / 2 &&
                unique(b) >= rows.length / 2) {
                return { type: "scatter", x: a, y: b };
            }
        }

        // Fallback: table
        if (catCols.length === 1 && numericCols.length === 1 && this.uniqueCount(rows, catCols[0]) > 20) {
            return { type: "table" };
        }

        // Refine using query text
        const q = (userQuery || "").toLowerCase();
        if (q.includes("trend") || q.includes("over time")) return { type: "line" };
        if (q.includes("top") || q.includes("compare")) return { type: "bar" };

        // Default fallback
        return { type: "table" };
    };

    // Chart data builders
    buildLineChartData = (rows, x, y) => ({
        labels: rows.map(r => String(r[x])),
        datasets: [{
            label: y,
            data: rows.map(r => Number(r[y])),
            borderColor: this.chartColors[0],
            backgroundColor: this.chartColors[0],
            fill: false,
        }]
    });

    buildBarChartData = (rows, x, y) => {
        const sorted = [...rows].sort((a, b) => Number(b[y] ?? 0) - Number(a[y] ?? 0)).slice(0, 20);
        return {
            labels: sorted.map(r => String(r[x])),
            datasets: [{
                label: y,
                data: sorted.map(r => Number(r[y])),
                backgroundColor: this.chartColors.slice(0, sorted.length),
            }]
        };
    };

    buildScatterChartData = (rows, x, y) => ({
        datasets: [{
            label: `${x} vs ${y}`,
            data: rows.map(r => ({ x: Number(r[x]), y: Number(r[y]) })),
            backgroundColor: this.chartColors[0],
        }]
    });

    buildPieChartData = (rows, labelCol, valueCol) => {
        const sorted = [...rows].sort((a, b) => Number(b[valueCol] ?? 0) - Number(a[valueCol] ?? 0)).slice(0, 20);
        return {
            labels: sorted.map(r => this.truncateLabel(r[labelCol])),
            datasets: [{
                data: sorted.map(r => Number(r[valueCol])),
                backgroundColor: this.chartColors.slice(0, sorted.length),
            }]
        };
    };

    //render chart
    renderChart = (table: IAiTableData & { query?: string }, index: number) => {
        const { dataArray, columnNames, query } = table;
        if (!Array.isArray(dataArray) || !Array.isArray(columnNames) || columnNames.length === 0) return null;
        const rows = dataArray.map(row =>
            Array.isArray(row)
                ? Object.fromEntries(columnNames.map((col, i) => [col, row[i]]))
                : row
        );
        const chartRec = this.chooseChart(columnNames, rows, query);
        if (chartRec.type === "line") {
            return (
                <div key={`ai-line-chart-${index}`} className="aiTableWrapper">
                    <Line
                        data={this.buildLineChartData(rows, chartRec.x, chartRec.y)}
                        options={{
                            responsive: true,
                            plugins: { legend: { display: false }, tooltip: { enabled: true } },
                            scales: {
                                x: {
                                    title: { display: true, text: chartRec.x },
                                    ticks: {
                                        callback: function (value) {
                                            let label = this.getLabelForValue(value as number);
                                            if (typeof label !== 'string') label = String(label);
                                            return label.length > 10 ? label.slice(0, 10) + '...' : label;
                                        }
                                    }
                                },
                                y: { title: { display: true, text: chartRec.y }, beginAtZero: true }
                            }
                        }}
                        height={100}
                        width={400}
                    />
                </div>
            );
        }
        if (chartRec.type === "bar") {
            return (
                <div key={`ai-bar-chart-${index}`} className="aiTableWrapper">
                    <Bar
                        data={this.buildBarChartData(rows, chartRec.x, chartRec.y)}
                        options={{
                            responsive: true,
                            plugins: { legend: { display: false }, tooltip: { enabled: true } },
                            scales: {
                                x: {
                                    title: { display: true, text: chartRec.x },
                                    ticks: {
                                        callback: function (value) {
                                            let label = this.getLabelForValue(value as number);
                                            if (typeof label !== 'string') label = String(label);
                                            return label.length > 10 ? label.slice(0, 10) + '...' : label;
                                        }
                                    }
                                },
                                y: { title: { display: true, text: chartRec.y }, beginAtZero: true }
                            }
                        }}
                        height={100}
                        width={400}
                    />
                </div>
            );
        }
        if (chartRec.type === "scatter") {
            return (
                <div key={`ai-scatter-chart-${index}`} className="aiTableWrapper">
                    <Scatter
                        data={this.buildScatterChartData(rows, chartRec.x, chartRec.y)}
                        options={{
                            responsive: true,
                            plugins: { legend: { display: false }, tooltip: { enabled: true } },
                            scales: {
                                x: { title: { display: true, text: chartRec.x }, type: 'linear', position: 'bottom' },
                                y: { title: { display: true, text: chartRec.y }, beginAtZero: true }
                            }
                        }}
                        height={100}
                        width={400}
                    />
                </div>
            );
        }
        if (chartRec.type === "pie") {
            return (
                <div
                    key={`ai-pie-chart-${index}`}
                    className="aiTableWrapper aiPieChartFlexCenter"
                >
                    <Pie
                        data={this.buildPieChartData(rows, chartRec.label, chartRec.value)}
                        options={{
                            responsive: false,
                            plugins: { legend: { position: 'right', display: true }, tooltip: { enabled: true } }
                        }}
                        height={400}
                        width={1000}
                    />
                </div>
            );
        }
        return null;
    };

    //render feedback UI
    renderFeedback = (msg: IAiChatMessage) => {
        if (msg.role !== 'assistant' || (!msg.text && !msg.tables)) return null;
        const state = this.getFeedback(msg.id);
        const feedback = state.feedback;
        const submitted = !!state.submitted;
        const showThankYou = submitted || feedback === 'up';
        const hasComment = !!String(state.comment || '').trim();
        return (
            <div className="aiFeedbackWrap">
                <div className="aiFeedbackHeader">
                    <span className="aiFeedbackLabel">Is this useful?</span>
                    <button
                        title="Yes"
                        aria-label="Thumbs up"
                        className={`aiFeedbackThumb ${!submitted && feedback === 'up' ? 'active-up' : ''} ${submitted ? 'is-disabled' : ''}`}
                        onClick={() => this.handleFeedback(msg.id, msg.serverMessageId, 'up')}
                        disabled={submitted}
                    >
                        <ThumbLikeRegular />
                    </button>
                    <button
                        title="No"
                        aria-label="Thumbs down"
                        className={`aiFeedbackThumb ${!submitted && feedback === 'down' ? 'active-down' : ''} ${submitted ? 'is-disabled' : ''}`}
                        onClick={() => this.handleFeedback(msg.id, msg.serverMessageId, 'down')}
                        disabled={submitted}
                    >
                        <ThumbDislikeRegular />
                    </button>
                </div>
                {feedback === 'down' && !submitted && (
                    <div className="aiFeedbackBox">
                        <textarea
                            className="aiFeedbackTextarea"
                            placeholder="Please tell us what was not useful..."
                            value={state.comment || ''}
                            onChange={e => this.handleFeedbackCommentChange(msg.id, e)}
                            maxLength={1000}
                            rows={2}
                        />
                        <button
                            type="button"
                            title="Submit feedback"
                            onClick={() => this.handleFeedbackSubmit(msg.id, msg.serverMessageId)}
                            className="aiFeedbackSubmitBtn"
                            aria-label="Submit feedback"
                            role="button"
                            disabled={!hasComment}
                        >
                            <span className="ms-Icon ms-Icon--Send" />
                        </button>
                    </div>
                )}
                {showThankYou && (
                    <div className="aiFeedbackThanks">Thank you for your feedback!</div>
                )}
            </div>
        );
    };

    getFeedback = (messageId: string) => this.state.feedbackState[messageId] || {};

    updateFeedback = (messageId: string, updates: { feedback?: 'up' | 'down', comment?: string, submitted?: boolean }) => {
        this.setState(prevState => ({
            feedbackState: {
                ...prevState.feedbackState,
                [messageId]: {
                    ...(prevState.feedbackState[messageId] || {}),
                    ...updates
                }
            }
        }));
    };

    handleFeedback = (messageId: string, serverMessageId: string | undefined, feedback: 'up' | 'down') => {
        const feedbackMessageId = serverMessageId || messageId;
        if (feedback === 'up') {
            SearchRequests.sendGenieFeedback(this.props.pageName, this.state.conversationId, feedbackMessageId, "POSITIVE", "");
            this.updateFeedback(messageId, { feedback, comment: '', submitted: true });
            return;
        }

        this.updateFeedback(messageId, { feedback, submitted: false });
    };

    handleFeedbackCommentChange = (messageId: string, e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.updateFeedback(messageId, { feedback: 'down', comment: e.target.value });
    };

    handleFeedbackSubmit = (messageId: string, serverMessageId: string | undefined) => {
        const state = this.getFeedback(messageId);
        const feedbackMessageId = serverMessageId || messageId;
        const comment = String(state.comment || "");
        SearchRequests.sendGenieFeedback(this.props.pageName, this.state.conversationId, feedbackMessageId, "NEGATIVE", comment);
        this.updateFeedback(messageId, { submitted: true });
    };

    render() {
        const { searchDisabled } = this.props;
        return (
            <div className="row searchView">
                <div className={`col-xs-12 aiChatPanel${this.state.inputAtBottom ? ' inputAtBottom' : ''}`}>
                    <div className={`aiChatMessages${this.state.inputAtBottom ? ' inputAtBottom' : ''}`} ref={this.aiMessagesRef}>
                        {(this.state.aiMessages || []).map((message, messageIndex) => {
                            if (message.role === "user") {
                                return (
                                    <div key={message.id} className="aiChatBubble aiUserBubble">
                                        <div className="aiBubbleLabel">You</div>
                                        <div className="aiBubbleContent">{this.formatCellValue(message.text)}</div>
                                    </div>
                                );
                            }
                            return (
                                <div key={message.id} className="aiChatBubble aiAssistantBubble">
                                    <div className="aiBubbleLabel">MatchAI</div>
                                    {message.loading ? (
                                        <div className="aiBubbleContent aiProgressText">
                                            <span className="aiProgressTextAnimated">
                                                {this.progressStages[(message.progressStage ?? 0)]}
                                            </span>
                                            <Skeleton active paragraph={{ rows: 3 }} title={false} style={{ marginTop: 5 }} />
                                        </div>
                                    ) : message.error ? (
                                        <div className="aiBubbleContent aiErrorText">{message.error}</div>
                                    ) : (
                                        <>
                                            {message.text && (
                                                <div className="aiBubbleContent">{this.formatCellValue(message.text)}</div>
                                            )}
                                            {message.tables && message.tables.length > 0 && (
                                                <div className="aiTableGrid">
                                                    {message.tables.map(this.renderAiTable)}
                                                    {message.tables.map(this.renderChart)}
                                                </div>
                                            )}
                                            {message.suggestedQuestions && message.suggestedQuestions.length > 0 && (
                                                <div className="aiSuggestions">
                                                    <div className="aiSectionTitle">Suggested follow-ups</div>
                                                    <div className="aiSuggestionChips">
                                                        {message.suggestedQuestions.map((question, index) => (
                                                            <button
                                                                key={`ai-suggestion-${messageIndex}-${index}`}
                                                                type="button"
                                                                className="aiSuggestionChip"
                                                                onClick={() => this.setState({ aiPrompt: question })}
                                                            >
                                                                <span className="aiSuggestionChipIcon">↪</span>{question}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {this.renderFeedback(message)}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className={`aiChatInputRow${this.state.inputAtBottom ? ' inputAtBottom' : ''}`}>
                        <div className={`aiChatInputBox${this.state.inputAtBottom ? ' inputAtBottom' : ''}`}>
                            <textarea
                                className={`aiChatInputTextarea${this.state.inputAtBottom ? ' inputAtBottom' : ''}`}
                                value={this.state.aiPrompt}
                                onChange={e => this.setState({ aiPrompt: e.target.value })}
                                disabled={searchDisabled === true}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey && !searchDisabled) {
                                        e.preventDefault();
                                        this.handleSubmit();
                                    }
                                }}
                                placeholder="Ask your question..."
                                maxLength={1000}
                                rows={1}
                            />
                            <button
                                type="button"
                                title="Send"
                                onClick={this.handleSubmit}
                                disabled={searchDisabled === true || !this.state.aiPrompt.trim()}
                                className="aiChatInputSendBtn"
                                aria-label="Send"
                                role="button"
                            >
                                <span className="ms-Icon ms-Icon--Send" />
                            </button>
                            <button
                                type="button"
                                title="Clear"
                                onClick={this.clearAiConversation}
                                disabled={searchDisabled === true}
                                className="aiChatInputClearBtn"
                                aria-label="Clear"
                                role="button"
                            >
                                <span className="ms-Icon ms-Icon--Clear" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default AiSearchView;
